import { FC, Fragment, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { CheckCodeSnippetConnection } from '@/features/dataSources/components/commons';
import { CheckInstallationMessage } from './CheckInstallationMessage';
import { CheckInstallationOperationList } from './CheckInstallationOperationList';
import { DataSource, DataSourceCodeConfig, EDataSourceStatus } from '@/models/dataSource';
import { useTranslation } from 'react-i18next';
import { Transition } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/solid';
import { useAppDispatch } from '@/reducers';
import { useWorkspace } from '@/features/workspaces/hooks';
import { dataSourcesApi, useGetDataSourceStatusQuery } from '@/services/dataSources';
import { stashDataSource } from '@/features/dataSources/dataSourcesSlice';

type CheckInstallationContentProps = {
  active: boolean;
  dataSource?: Partial<DataSource<DataSourceCodeConfig>>;
  onActiveComplete: VoidFunction;
  goNextStep: VoidFunction;
};

export const CheckInstallationContent: FC<CheckInstallationContentProps> = ({ active, dataSource, onActiveComplete, goNextStep }) => {
  const dispatch = useAppDispatch();
  const workspace = useWorkspace();
  const { t } = useTranslation('data_source_edit');

  const [pollingInterval, setPollingInterval] = useState(5000);
  const { data } = useGetDataSourceStatusQuery(
    {
      workspaceId: workspace.id,
      dataSourceId: dataSource?.id || '',
    },
    {
      pollingInterval,
    }
  );

  useEffect(() => {
    if (data?.status === EDataSourceStatus.ACTIVE) {
      setPollingInterval(0);
      onActiveComplete();

      dispatch(dataSourcesApi.util.invalidateTags(['DataSources']));

      dispatch(
        stashDataSource({
          ...dataSource,
          status: EDataSourceStatus.ACTIVE,
        })
      );

      goNextStep();
    }
  }, [data]);

  return (
    <div className='relative mt-6 h-auto rounded-lg border border-solid border-gray-200 p-6 text-left'>
      <div className={twMerge('flex flex-col gap-4 z-10 rounded transition duration-1000 ease-linear', active && 'opacity-20 backdrop-blur-sm duration-75')}>
        <div className='flex flex-col gap-2'>
          <h1 className='text-base font-bold leading-5 text-gray-900'>{t('code.check_installation.is_it_working')}</h1>
          <CheckInstallationMessage text='snippet_install' />
        </div>

        <CheckInstallationOperationList siteUrl={dataSource?.configuration?.url} />

        <div>
          <CheckInstallationMessage text='installed_but_not_working' />
          <CheckInstallationMessage classNames='font-normal' text='do_not_worry_keep_waiting' />
        </div>

        <div>
          <CheckCodeSnippetConnection dataSource={dataSource} />
        </div>
      </div>
      <Transition
        as={Fragment}
        show={active}
        enter='transition ease-linear duration-75'
        enterFrom='opacity-0'
        enterTo='opacity-100'
        leave='ease-linear duration-1000'
        leaveFrom='opacity-100'
        leaveTo='opacity-0'
      >
        <div className='absolute left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2'>
          <div className='inline-block place-items-center'>
            <CheckIcon className='mx-auto mb-2 h-8 rounded-full bg-green-100 p-1 text-green-600' />
            <h3 className='text-gray-900'>{t('code.check_installation.connected_successfully')}</h3>
          </div>
        </div>
      </Transition>
    </div>
  );
};
